import { useEffect, useState, useSyncExternalStore } from 'react';
import type { Root } from 'react-dom/client';
import { KeyRound, X } from 'lucide-react';
import { Waiting } from '@/components/Waiting';
import { subscribeToVaultFallback, vaultFellBack } from '@/services/refreshSecretVault';
import { isDesktopRuntime } from './desktopDetection';
import { noteLaunchMilestone } from './launchMilestones';
import { LocaleProvider, useT } from '@/i18n/LocaleProvider';

/**
 * RES-13a (I-1696, `docs/concept-desktop-resilience.md`): what the desktop window shows while its
 * stored sign-in is put back, before the app mounts.
 *
 * The restore must finish before the app asks whether there is a session (`desktopSessionHydration`),
 * so the app waits for it; the window does not. A keychain that is slow to answer used to leave the
 * window blank, which reads as a crashed app.
 */
export function RestoringSession({ reconnecting = false }: { reconnecting?: boolean }) {
  // Before the app mounts the reader's language may not be loaded yet: its provider says this line in
  // their language all the same, from the words the entry carries for it (I-1971).
  const t = useT();
  // T-0896 (LC-6i): a step of the launch the launch smoke shows, noted once the line is on screen.
  useEffect(() => noteLaunchMilestone('restoring session painted'), []);
  return (
    <div
      role="status"
      data-testid="restoring-session"
      data-reconnecting={reconnecting ? '' : undefined}
      className="flex min-h-screen flex-col items-center justify-center gap-4 bg-white px-4 text-center text-sm text-gray-600 dark:bg-gray-950 dark:text-gray-300"
    >
      <Waiting size="launch" silent />
      {reconnecting ? (
        // I-2572 (MS-2): a keychain read failed and is tried again. Never the sign-in screen.
        <div className="flex max-w-sm flex-col gap-1">
          <p className="font-medium text-gray-800 dark:text-gray-100">
            {t('navigation.desktop.reconnecting')}
          </p>
          <p className="text-[13px] text-gray-500 dark:text-gray-400">
            {t('navigation.desktop.keychainWaiting')}
          </p>
        </div>
      ) : (
        <p>{t('navigation.desktop.restoringSession')}</p>
      )}
    </div>
  );
}

/**
 * Paint the restoring state into `root` now, in the desktop app only; the caller renders the app
 * into the same root once the restore settles. ⛔ *Mutation:* render nothing here → the window is
 * blank until the restore settles → `restoringSession.test.tsx` red.
 */
export function showRestoringSession(root: Root, options: { reconnecting?: boolean } = {}): void {
  if (!isDesktopRuntime()) return;
  root.render(
    <LocaleProvider>
      <RestoringSession reconnecting={options.reconnecting} />
    </LocaleProvider>,
  );
}

/**
 * RES-13c: one line when the web's vault would not take the sign-in, so it is kept in memory for
 * this run. The desktop keychain still holds it, so the next start restores it again; nothing is
 * lost, and nothing asks the person to act. Desktop only: a browser with no vault has said so
 * since I-1625 and gains nothing from a notice.
 */
export function KeychainFallbackNotice() {
  const t = useT();
  const fellBack = useSyncExternalStore(subscribeToVaultFallback, vaultFellBack, () => false);
  const [dismissed, setDismissed] = useState(false);
  if (!isDesktopRuntime() || !fellBack || dismissed) return null;
  return (
    <div
      role="status"
      data-testid="keychain-fallback-notice"
      className="fixed bottom-[calc(1rem+var(--bottom-bars-height,0px))] left-1/2 z-50 flex w-[min(26rem,calc(100vw-2rem))] -translate-x-1/2 items-center gap-2.5 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[13px] text-amber-950 shadow-lg dark:border-amber-900/50 dark:bg-[#2a2418] dark:text-amber-100"
    >
      <KeyRound className="h-4 w-4 shrink-0" aria-hidden="true" />
      <p className="min-w-0 flex-1">{t('navigation.desktop.keychainFallback')}</p>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        aria-label={t('navigation.sidebar.dismiss')}
        className="-m-1 rounded-sm p-1 text-amber-800 transition-colors hover:bg-amber-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-focus dark:text-amber-200 dark:hover:bg-white/10"
      >
        <X className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
    </div>
  );
}
